import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useLocation, useNavigate } from 'react-router-dom';
import { apiFetchAuth, apiFetchGuest } from '../../api/client';
import { getToken, isLoggedIn } from '../../auth/auth';
import { requireLoginOrContinue } from '../../auth/returnTo';
import { getGuestPaymentId } from '../../payments/guestIdentity';
import { fetchLatestGuestChemistryResult } from '../../payments/client';
import { disableGuestAnalysisMode, enableGuestAnalysisMode, hasGuestNavigationIntent, isGuestAnalysisMode } from '../../payments/guestAnalysisMode';
import './Chemistry.css';
import NavBar from '../../components/NavBar.jsx';
import ChemistryLoading from './Loading.jsx';
import { useMyPageDashboard } from '../../hooks/useMyPageDashboard';
import { pickNativeImage, isNativeApp } from '../../config/capacitor';
import {
  buildChemistryRecoveryPayload,
  writeStoredChemistryResult,
  writeChemistryDraft,
  readChemistryDraft,
  readStoredChemistryResult,
  clearChemistryDraft,
  readChemistryPending,
  writeChemistryPending,
  clearChemistryPending,
  clearStoredChemistryResult,
  createChemistrySessionId,
  resetChemistryRecovery,
} from '../../utils/chemistryStorage';
import {
  createEmptyImageSlotState,
  restoreImageSlotState,
  prepareImageUploadAsset,
  getImageInputErrorMessage,
} from '../../utils/imageUpload';
import {
  getAnalysisFailureReason,
  getDurationBucket,
  getFileSizeBucket,
  getFileType,
  getPhotoFailureReason,
  getScoreBucket,
  trackEvent,
} from '../../utils/analytics';
import { formatPerUsePrice, formatProductPrice, getChemistryPaymentProducts } from '../../payments/catalog';
import { useMarket } from '../../payments/useMarket';
import { useTranslation } from 'react-i18next';
import { getLocalizedError } from '../../api/errors';
import { localize } from '../../i18n/locale';

const RECOVERY_MESSAGES = {
  expired: { ko: '저장된 궁합 분석 데이터가 만료되었습니다. 새로 시작해 주세요.', en: 'The saved chemistry data expired. Please start again.' },
  corrupt: { ko: '저장된 궁합 분석 데이터를 복구할 수 없습니다. 새로 시작해 주세요.', en: 'The saved chemistry data could not be restored. Please start again.' },
  version_mismatch: { ko: '이전 버전 데이터라 복구할 수 없습니다. 새로 시작해 주세요.', en: 'This data uses an older format and cannot be restored. Please start again.' },
};

function getRecoveryMessage(label, status, language) {
  const base = localize(language, RECOVERY_MESSAGES[status]);
  if (!base) return '';
  return label ? `${label} ${base}` : base;
}

function createInitialImages() {
  return {
    A: createEmptyImageSlotState('A'),
    B: createEmptyImageSlotState('B'),
  };
}

function slotStatus(slotState, nameValue, language) {
  if (slotState.previewUrl && nameValue.trim()) return localize(language, { ko: '준비 완료', en: 'Ready' });
  if (slotState.previewUrl) return localize(language, { ko: '사진 선택 완료', en: 'Photo Ready' });
  if (nameValue.trim()) return localize(language, { ko: '이름 입력 완료', en: 'Name Ready' });
  return localize(language, { ko: '입력 필요', en: 'Input Needed' });
}

function slotHint(slotState, language) {
  if (!slotState.previewUrl) return '';

  const parts = [localize(language, { ko: `JPEG 준비 ${slotState.uploadSizeLabel}`, en: `JPEG ready ${slotState.uploadSizeLabel}` })];
  if (slotState.hadHeicSource) parts.push(localize(language, { ko: 'HEIC 변환', en: 'HEIC converted' }));
  if (slotState.source === 'camera') parts.push(localize(language, { ko: '카메라', en: 'Camera' }));
  if (slotState.source === 'photos') parts.push(localize(language, { ko: '사진 보관함', en: 'Photo library' }));
  if (slotState.source === 'file') parts.push(localize(language, { ko: '파일 선택', en: 'File' }));
  return parts.join(' · ');
}

function ChemistryInfo() {
  const navigate = useNavigate();
  const location = useLocation();
  const { i18n } = useTranslation();
  const language = i18n.resolvedLanguage || i18n.language;
  const tr = (ko, en) => localize(language, { ko, en });
  const dashboard = useMyPageDashboard();
  const sessionIdRef = useRef(createChemistrySessionId());
  const didHydrateRef = useRef(false);
  const selectionRequestRef = useRef({ A: 0, B: 0 });
  const attemptCountRef = useRef(0);
  const pageViewTrackedRef = useRef(false);

  const [images, setImages] = useState(createInitialImages);
  const [nameA, setNameA] = useState('');
  const [nameB, setNameB] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [recoveryNotice, setRecoveryNotice] = useState('');
  const [packageModalOpen, setPackageModalOpen] = useState(false);
  const [loginChoiceOpen, setLoginChoiceOpen] = useState(false);
  const [guestMode, setGuestMode] = useState(() => !isLoggedIn() && isGuestAnalysisMode());
  const [guestAnalysisLimitReached, setGuestAnalysisLimitReached] = useState(false);
  const [latestGuestResult, setLatestGuestResult] = useState(null);
  const { data: marketData } = useMarket();
  const chemistryPaymentProducts = getChemistryPaymentProducts(marketData.market);

  useEffect(() => {
    if (isLoggedIn()) {
      disableGuestAnalysisMode();
      setGuestMode(false);
      return;
    }
    if (hasGuestNavigationIntent(location.state)) {
      enableGuestAnalysisMode();
      getGuestPaymentId();
      setGuestMode(true);
    }
  }, [location.state]);

  const imageA = images.A;
  const imageB = images.B;
  const canStart = Boolean(imageA.file && imageB.file && nameA.trim() && nameB.trim());
  const chemistryAttemptCount = dashboard.chemistryResults?.length || 0;
  const chemistryEntitlement = dashboard.entitlements?.chemistry_premium;
  const chemistryRemainingUses = typeof chemistryEntitlement === 'object'
    ? Number(chemistryEntitlement.uses_remaining || 0)
    : Number(Boolean(chemistryEntitlement));
  const hasChemistryPremium = chemistryRemainingUses > 0;
  const needsChemistryPayment = isLoggedIn() && !hasChemistryPremium && chemistryAttemptCount >= 1;
  const needsDashboardCheck = isLoggedIn() && dashboard.loading;

  const openPaymentForProduct = (productId) => {
    navigate('/payments/start/' + productId + '?returnTo=%2Fchemistry-test%2Finfo');
  };

  const openPaywall = (sourcePage) => {
    trackEvent('paywall_viewed', {
      analysis_type: 'chemistry',
      product_id: 'chemistry_pass',
      source_page: sourcePage,
      market: marketData.market,
      currency: marketData.currency,
    });
    setPackageModalOpen(true);
  };

  const openLatestGuestResult = () => {
    if (!latestGuestResult?.id) return;
    const stored = readStoredChemistryResult();
    const storedData = stored.status === "available" ? stored.data?.data : null;
    const data = String(storedData?.id || "") === String(latestGuestResult.id)
      ? storedData
      : latestGuestResult;
    const previewA = data?.input_images?.a || stored.data?.previewA || "";
    const previewB = data?.input_images?.b || stored.data?.previewB || "";
    if (!storedData || String(storedData.id || "") !== String(data.id || "")) {
      writeStoredChemistryResult(
        buildChemistryRecoveryPayload({
          data,
          previewA,
          previewB,
          nameA: data?.user_names?.a || "",
          nameB: data?.user_names?.b || "",
        }),
        { sessionId: String(data.id) },
      );
    }
    navigate("/chemistry-test/result", {
      state: { data, previewA, previewB, imageA: previewA, imageB: previewB },
    });
  };

  const resetFlowArtifacts = () => {
    clearChemistryPending();
    clearStoredChemistryResult();
  };

  const restoreDraftLikeState = (restored) => {
    if (!restored) return;

    sessionIdRef.current = restored.sessionId || createChemistrySessionId();
    setNameA(restored.nameA || '');
    setNameB(restored.nameB || '');
    setImages({
      A: restoreImageSlotState(restored.previewA || '', 'chemistry-draft-a', 'A'),
      B: restoreImageSlotState(restored.previewB || '', 'chemistry-draft-b', 'B'),
    });
  };

  const handleFreshStart = () => {
    resetChemistryRecovery();
    sessionIdRef.current = createChemistrySessionId();
    selectionRequestRef.current = { A: 0, B: 0 };
    setImages(createInitialImages());
    setNameA('');
    setNameB('');
    setError('');
    setRecoveryNotice(tr('저장된 분석 데이터를 지우고 새로 시작합니다.', 'Saved analysis data was cleared. Starting fresh.'));
  };

  useEffect(() => {
    const notices = [];
    if (!pageViewTrackedRef.current) {
      pageViewTrackedRef.current = true;
      trackEvent('analysis_page_viewed', {
        analysis_type: 'chemistry',
        entry_source: 'direct_or_navigation',
      });
    }

    const pendingState = readChemistryPending();
    const draftState = readChemistryDraft();

    if (pendingState.status === 'available' && pendingState.data) {
      restoreDraftLikeState(pendingState.data);
      notices.push(tr('이전에 하던 궁합분석 입력 상태를 불러왔습니다.', 'Your previous chemistry analysis input was restored.'));
    } else if (draftState.status === 'available' && draftState.data) {
      restoreDraftLikeState(draftState.data);
      notices.push(tr('임시 저장된 궁합 입력 상태를 불러왔습니다.', 'Your saved chemistry draft was restored.'));
    }

    if (pendingState.issue) {
      notices.push(getRecoveryMessage(tr('진행 중 데이터 복구에 실패했습니다.', 'Could not restore in-progress data.'), pendingState.issue.status, language));
    }
    if (draftState.issue) {
      notices.push(getRecoveryMessage(tr('임시 저장 데이터 복구에 실패했습니다.', 'Could not restore draft data.'), draftState.issue.status, language));
    }

    if (notices.length > 0) {
      setRecoveryNotice(notices.join(' '));
    }

    didHydrateRef.current = true;
  }, []);

  useEffect(() => {
    if (!didHydrateRef.current) return;

    const nextSessionId = writeChemistryDraft({
      sessionId: sessionIdRef.current,
      nameA,
      nameB,
      previewA: imageA.previewUrl,
      previewB: imageB.previewUrl,
    });

    if (nextSessionId) {
      sessionIdRef.current = nextSessionId;
    }
  }, [nameA, nameB, imageA.previewUrl, imageB.previewUrl]);

  const handleNameChange = (target, value) => {
    resetFlowArtifacts();
    setRecoveryNotice('');
    if (target === 'A') setNameA(value);
    else setNameB(value);
  };

  const setPreparedImage = (target, asset) => {
    resetFlowArtifacts();
    setRecoveryNotice('');
    setImages((current) => ({ ...current, [target]: asset }));
  };

  const handlePreparedImage = async (target, inputPromise) => {
    const requestId = (selectionRequestRef.current[target] || 0) + 1;
    selectionRequestRef.current[target] = requestId;

    try {
      setError('');
      const asset = await inputPromise;
      if (!asset || selectionRequestRef.current[target] !== requestId) return;
      setPreparedImage(target, asset);
      const photoSlot = target === 'A' ? 'person_a' : 'person_b';
      trackEvent('photo_selected', {
        analysis_type: 'chemistry',
        photo_slot: photoSlot,
        file_type: getFileType(asset.sourceType),
        file_size_bucket: getFileSizeBucket(asset.originalSizeBytes),
      });
      trackEvent('chemistry_person_ready', {
        analysis_type: 'chemistry',
        person_slot: photoSlot,
      });
    } catch (inputError) {
      if (selectionRequestRef.current[target] !== requestId) return;
      const nextMessage = getImageInputErrorMessage(inputError);
      if (!nextMessage) return;
      console.error(`Failed to prepare image for slot ${target}:`, inputError);
      trackEvent('photo_validation_failed', {
        analysis_type: 'chemistry',
        photo_slot: target === 'A' ? 'person_a' : 'person_b',
        failure_reason: getPhotoFailureReason(inputError),
      });
      setError(nextMessage);
    }
  };

  const handleFileChange = async (event, target) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file) return;

    await handlePreparedImage(
      target,
      prepareImageUploadAsset({
        file,
        filenameBase: `chemistry-${target.toLowerCase()}-${Date.now()}`,
        slot: target,
        source: 'file',
      }),
    );
  };

  const handleNativeImagePick = async (source, target) => {
    trackEvent('photo_select_started', {
      analysis_type: 'chemistry',
      photo_slot: target === 'A' ? 'person_a' : 'person_b',
    });
    await handlePreparedImage(
      target,
      (async () => {
        const picked = await pickNativeImage(source, `chemistry-${target.toLowerCase()}-${Date.now()}`);
        if (!picked?.dataUrl) return null;
        return prepareImageUploadAsset({
          dataUrl: picked.dataUrl,
          filenameBase: `chemistry-${target.toLowerCase()}-${Date.now()}`,
          slot: target,
          source,
        });
      })(),
    );
  };

  const handleAnalysis = async ({ guestOverride = false } = {}) => {
    if (!canStart) {
      setError(tr('두 사람 이름과 사진을 모두 입력한 뒤 시작해 주세요.', 'Enter both names and select both photos before starting.'));
      return;
    }

    if (!isLoggedIn() && !guestMode && !guestOverride) {
      setLoginChoiceOpen(true);
      setRecoveryNotice(tr('로그인하거나 비회원으로 분석을 계속할 수 있습니다.', 'Log in or continue the analysis as a guest.'));
      return;
    }

    if (needsDashboardCheck) {
      setError(tr('이용 가능 여부를 확인하는 중입니다. 잠시 후 다시 시도해 주세요.', 'Checking availability. Please try again shortly.'));
      return;
    }

    if (needsChemistryPayment) {
      openPaywall('chemistry_input');
      return;
    }

    try {
      setLoading(true);
      setError('');
      setRecoveryNotice('');

      const sessionId = sessionIdRef.current || createChemistrySessionId();
      sessionIdRef.current = sessionId;

      writeChemistryPending({
        sessionId,
        nameA,
        nameB,
        previewA: imageA.previewUrl,
        previewB: imageB.previewUrl,
      });

      const formData = new FormData();
      formData.append('image_a', imageA.file);
      formData.append('image_b', imageB.file);
      formData.append('user_name_a', nameA);
      formData.append('user_name_b', nameB);
      formData.append('locale', language);

      const requestOptions = { method: 'POST', body: formData };
      const attemptNumber = attemptCountRef.current + 1;
      attemptCountRef.current = attemptNumber;
      const startedAt = performance.now();
      trackEvent('analysis_started', {
        analysis_type: 'chemistry',
        attempt_number: attemptNumber,
        is_logged_in: isLoggedIn(),
        user_mode: isLoggedIn() ? 'member' : 'guest',
        credit_required: chemistryAttemptCount >= 1,
      });
      const data = getToken()
        ? await apiFetchAuth('/analyze/chemistry', requestOptions)
        : await apiFetchGuest('/analyze/chemistry', requestOptions);
      clearStoredChemistryResult();
      const chemistryScore = data?.scores?.chemistry;
      const hasUsableResult = Boolean(
        data &&
        data.status !== 'fail' &&
        !data.message &&
        data.scores &&
        data.user_names
      );
      if (hasUsableResult) {
        trackEvent('analysis_completed', {
          analysis_type: 'chemistry',
          attempt_number: attemptNumber,
          duration_bucket: getDurationBucket(performance.now() - startedAt),
          result_type: data.type_code || data.result_type,
          score_bucket: getScoreBucket(chemistryScore),
          user_mode: isLoggedIn() ? 'member' : 'guest',
        });
        if (chemistryAttemptCount >= 1 && data.premium_unlocked === true) {
          trackEvent('credit_used', {
            analysis_type: 'chemistry',
            credit_amount: 1,
          });
        }
      } else {
        trackEvent('analysis_failed', {
          analysis_type: 'chemistry',
          attempt_number: attemptNumber,
          failure_reason: 'invalid_response',
        });
      }
      if (import.meta.env.DEV) {
        console.info('[ChemistryAnalysis] mesh_overlay_data_url exists', Boolean(data?.mesh_overlays?.a || data?.mesh_overlays?.b));
        console.info('[ChemistryAnalysis] input_image_data_url exists', Boolean(data?.input_images?.a || data?.input_images?.b));
      }

      const responsePreviewA = data?.input_images?.a || imageA.previewUrl;
      const responsePreviewB = data?.input_images?.b || imageB.previewUrl;

      writeStoredChemistryResult(
        buildChemistryRecoveryPayload({
          data,
          previewA: responsePreviewA,
          previewB: responsePreviewB,
          nameA,
          nameB,
        }),
        { sessionId },
      );
      clearChemistryDraft();

      navigate('/chemistry-test/result', {
        state: {
          data,
          previewA: responsePreviewA,
          previewB: responsePreviewB,
          imageA: responsePreviewA,
          imageB: responsePreviewB,
          input_images: data?.input_images || null,
          mesh_overlays: data?.mesh_overlays || null,
          playAnalysisSequence: true,
        },
      });
    } catch (requestError) {
      trackEvent('analysis_failed', {
        analysis_type: 'chemistry',
        attempt_number: attemptCountRef.current,
        failure_reason: getAnalysisFailureReason(requestError),
        http_status: Number(requestError?.status || 0) || undefined,
      });
      clearChemistryPending();
      console.error('Chemistry upload or analysis failed:', requestError);
      const message = getLocalizedError(requestError, language);
      setError(message);
      const paymentRequired = requestError?.code === 'chemistry_payment_required' || requestError?.status === 402;
      if (paymentRequired) {
        if (isLoggedIn()) {
          openPaywall('chemistry_api_error');
        } else {
          setGuestAnalysisLimitReached(true);
          setError(tr('비회원 무료 분석은 1회까지입니다. 기존 결과를 보거나 로그인 후 추가 분석을 진행해 주세요.', 'Guest analysis is limited to one free result. View your existing result or sign in for another analysis.'));
          try {
            const latest = await fetchLatestGuestChemistryResult();
            setLatestGuestResult(latest?.result || null);
          } catch {
            setLatestGuestResult(null);
          }
        }
      }
      setRecoveryNotice(paymentRequired && !isLoggedIn()
        ? ''
        : tr('선택한 사진과 이름은 유지되며 바로 다시 시도할 수 있습니다.', 'Your selected photos and names were kept so you can retry immediately.'));
      setLoading(false);
    }
  };

  if (loading) {
    return <ChemistryLoading />;
  }

  const resultPreviewCards = [
    {
      title: tr('시선 끌림', 'Visual Pull'),
      body: tr('첫 화면에서 누가 더 눈에 들어오는지', 'Who draws attention first at a glance'),
      accent: 'pink',
    },
    {
      title: tr('편안함', 'Comfort'),
      body: tr('닮은 분위기에서 오는 안정감', 'The calm that comes from a familiar mood'),
      accent: 'mint',
    },
    {
      title: tr('반응 차이', 'Reaction Gap'),
      body: tr('A→B, B→A의 시선 흐름 차이', 'How A to B and B to A attention flows differently'),
      accent: 'indigo',
    },
  ];

  const relationTypes = [
    tr('운명적 울림', 'Fated Resonance'),
    tr('본능적 인력', 'Instinctive Pull'),
    tr('편안한 안식', 'Soft Shelter'),
    tr('독립적 행성', 'Independent Planets'),
  ];

  const trustCards = [
    {
      title: tr('포즈 불변 분석', 'Pose-Aware Analysis'),
      body: tr('사진 각도가 조금 달라도 얼굴 구조를 보정해 분석해요.', 'Even when angles vary, facial structure is normalized before analysis.'),
    },
    {
      title: 'Mirror & Magnet',
      body: tr('닮은 점과 다른 점을 함께 계산해 궁합 타입을 보여줘요.', 'Similarities and differences are calculated together to shape the chemistry type.'),
    },
  ];

  const renderSlot = (slotKey, label, description, nameValue, onNameChange, slotState) => (
    <div className={
      'chemistry-landing-slot ' +
      (slotState.previewUrl ? 'has-preview ' : '') +
      (slotKey === 'A' ? 'slot-a' : 'slot-b')
    }>
      <div className="chemistry-slot-topline">
        <div>
          <span className="chemistry-slot-label">{label}</span>
          <p>{description}</p>
        </div>
        <span className="chemistry-status-pill">{slotStatus(slotState, nameValue, language)}</span>
      </div>

      <label
        className="chemistry-photo-dropzone"
        onClick={() => trackEvent('photo_select_started', {
          analysis_type: 'chemistry',
          photo_slot: slotKey === 'A' ? 'person_a' : 'person_b',
        })}
      >
        <input
          type="file"
          accept="image/*,.heic,.heif"
          onChange={(event) => handleFileChange(event, slotKey)}
          disabled={loading}
        />
        {slotState.previewUrl ? (
          <img src={slotState.previewUrl} alt={tr(label + ' 미리보기', label + ' preview')} className="chemistry-photo-preview" />
        ) : (
          <div className="chemistry-photo-placeholder" aria-hidden="true">
            <div className="chemistry-silhouette">
              <span />
              <strong />
            </div>
            <em>{tr('정면 사진 올리기', 'Upload a front-facing photo')}</em>
          </div>
        )}
      </label>

      <input
        type="text"
        placeholder={tr(label + ' 이름', label + ' name')}
        value={nameValue}
        onChange={(e) => onNameChange(e.target.value)}
        disabled={loading}
        className="chemistry-name-input"
      />

      {slotHint(slotState, language) ? (
        <div className="chemistry-slot-hint">{slotHint(slotState, language)}</div>
      ) : null}

      {isNativeApp() ? (
        <div className="chemistry-native-actions">
          <button type="button" onClick={() => handleNativeImagePick('photos', slotKey)} disabled={loading}>
            {tr('갤러리', 'Gallery')}
          </button>
          <button type="button" onClick={() => handleNativeImagePick('camera', slotKey)} disabled={loading}>
            {tr('카메라', 'Camera')}
          </button>
        </div>
      ) : null}
    </div>
  );

  return (
    <div className="chemistry-landing-page">
      <NavBar theme="dark" />
      <main className="chemistry-landing-main">
        <section className="chemistry-hero-panel" aria-labelledby="chemistry-hero-title">
          <div className="chemistry-hero-copy">
            <span className="chemistry-kicker">{tr('첫인상 궁합 리포트', 'First-Impression Chemistry Report')}</span>
            <h1 id="chemistry-hero-title">{tr('우리 둘, 누가 더 끌릴까?', 'Between us, who feels the stronger pull?')}</h1>
            <p className="chemistry-hero-subcopy">
              {tr('사진 2장으로 보는 첫인상 궁합 리포트. 시선 끌림, 편안함, 반응 차이를 분석해 드려요.', 'A first-impression chemistry report from two photos. We analyze visual pull, comfort, and reaction differences.')}
            </p>
            <p className="chemistry-hero-note">
              {tr('정면 사진을 올리면 약 10초 안에 결과를 확인할 수 있어요.', 'Upload front-facing photos and see your result in about 10 seconds.')}
            </p>
          </div>

          {error ? <div className="chemistry-message is-error">{error}</div> : null}

          {recoveryNotice ? (
            <div className="chemistry-message is-info">
              <span>{recoveryNotice}</span>
              <button type="button" onClick={handleFreshStart} disabled={loading}>
                {tr('새로 시작', 'Start Over')}
              </button>
            </div>
          ) : null}

          {guestAnalysisLimitReached && !isLoggedIn() ? (
            <div className="chemistry-message is-info">
              <span>{tr('새 결과가 없으므로 결과 단위 결제를 시작할 수 없습니다.', 'There is no new result to unlock, so a result-based payment cannot be started.')}</span>
              {latestGuestResult?.id ? (
                <button type="button" onClick={openLatestGuestResult}>
                  {tr('기존 결과 보기', 'View Existing Result')}
                </button>
              ) : null}
              <button type="button" onClick={() => requireLoginOrContinue({ source: 'chemistry_guest_limit' })}>
                {tr('로그인하고 추가 분석하기', 'Sign In for Another Analysis')}
              </button>
            </div>
          ) : null}

          <div className="chemistry-upload-stage" aria-label={tr('궁합 분석 사진 업로드', 'Chemistry photo upload')}>
            {renderSlot(
              'A',
              tr('나', 'Me'),
              tr('내 첫인상을 기준으로 분석해요.', 'Used as your first-impression baseline.'),
              nameA,
              (value) => handleNameChange('A', value),
              imageA,
            )}
            <div className="chemistry-connector" aria-hidden="true">
              <span className="chemistry-connector-line" />
              <strong>♡</strong>
              <span className="chemistry-connector-line" />
            </div>
            {renderSlot(
              'B',
              tr('상대', 'Partner'),
              tr('상대의 분위기와 반응을 비교해요.', 'Compared for mood and reaction flow.'),
              nameB,
              (value) => handleNameChange('B', value),
              imageB,
            )}
          </div>

          <div className="chemistry-readiness-row" aria-live="polite">
            <span>{nameA.trim() && nameB.trim() ? tr('이름 입력 완료', 'Names Ready') : tr('이름 2개 필요', 'Two Names Needed')}</span>
            <span>{imageA.file && imageB.file ? tr('사진 선택 완료', 'Photos Ready') : tr('사진 2장 필요', 'Two Photos Needed')}</span>
            <span>
              {hasChemistryPremium
                ? tr('남은 이용권 ' + chemistryRemainingUses + '회', chemistryRemainingUses + ' passes remaining')
                : tr('무료 1회 기준 ' + Math.min(chemistryAttemptCount, 1) + '/1 사용', 'Free use: ' + Math.min(chemistryAttemptCount, 1) + '/1 used')}
            </span>
          </div>

          {needsChemistryPayment ? (
            <div className="chemistry-message is-info">
              <span>{tr('무료 1회는 이미 사용했습니다. 다음 궁합분석부터는 이용권이 필요합니다.', 'Your free analysis has been used. Another analysis requires a pass.')}</span>
              <button type="button" onClick={() => openPaywall('chemistry_input_notice')}>
                {tr('이용권 선택', 'Choose Pass')}
              </button>
            </div>
          ) : null}

          <button
            type="button"
            onClick={() => handleAnalysis()}
            disabled={!canStart || needsDashboardCheck || guestAnalysisLimitReached}
            className="chemistry-main-cta"
          >
            {guestAnalysisLimitReached
              ? tr('기존 결과를 보거나 로그인해 주세요', 'View Your Result or Sign In')
              : needsDashboardCheck
                ? tr('이용 가능 여부 확인 중', 'Checking Availability')
              : needsChemistryPayment
                ? tr('추가 궁합분석 결제하기', 'Purchase Another Analysis')
                : canStart
                  ? tr('우리 궁합 결과 보기', 'See Our Chemistry Result')
                  : tr('사진 2장으로 궁합 분석 시작하기', 'Start with Two Photos')}
          </button>
        </section>

        <section className="chemistry-preview-section" aria-labelledby="chemistry-preview-title">
          <div className="chemistry-section-heading">
            <span>{tr('결과 미리보기', 'Result Preview')}</span>
            <h2 id="chemistry-preview-title">{tr('이런 결과를 받아볼 수 있어요', 'What your report can reveal')}</h2>
          </div>
          <div className="chemistry-preview-grid">
            {resultPreviewCards.map((card) => (
              <article key={card.title} className={'chemistry-preview-card accent-' + card.accent}>
                <h3>{card.title}</h3>
                <p>{card.body}</p>
              </article>
            ))}
          </div>
        </section>

        <section className="chemistry-type-section" aria-label={tr('관계 타입 미리보기', 'Relationship type preview')}>
          <div className="chemistry-type-copy">
            <span>{tr('관계 타입', 'Chemistry Types')}</span>
            <h2>{tr('둘의 분위기는 어떤 타입일까요?', 'What mood will the two of you create?')}</h2>
          </div>
          <div className="chemistry-type-chips">
            {relationTypes.map((type) => (
              <span key={type}>{type}</span>
            ))}
          </div>
        </section>

        <section className="chemistry-trust-section" aria-labelledby="chemistry-trust-title">
          <div className="chemistry-section-heading">
            <span>{tr('분석 방식', 'Analysis Method')}</span>
            <h2 id="chemistry-trust-title">{tr('왜 믿을 수 있나요?', 'Why is it credible?')}</h2>
          </div>
          <div className="chemistry-trust-grid">
            {trustCards.map((card) => (
              <article key={card.title} className="chemistry-trust-card">
                <h3>{card.title}</h3>
                <p>{card.body}</p>
              </article>
            ))}
          </div>
        </section>
      </main>

      {loginChoiceOpen && typeof document !== "undefined" ? createPortal(
        <div className="chem-package-modal-backdrop" role="presentation" onClick={() => setLoginChoiceOpen(false)}>
          <div className="chem-package-modal" role="dialog" aria-modal="true" aria-labelledby="chemistry-login-choice-title" onClick={(event) => event.stopPropagation()}>
            <div className="chem-package-modal-header">
              <button type="button" className="chem-package-close" onClick={() => setLoginChoiceOpen(false)} aria-label={tr("닫기", "Close")}>x</button>
              <h2 id="chemistry-login-choice-title">{tr("어떻게 진행할까요?", "How would you like to continue?")}</h2>
              <p>{tr("로그인하면 회원 기록에 저장되고, 비회원은 이 브라우저에서 결과를 이어볼 수 있습니다.", "Sign in to save to your account, or continue as a guest on this browser.")}</p>
            </div>
            <div className="chem-package-stack">
              <button type="button" className="chem-package-pay-btn is-recommended" onClick={() => {
                setLoginChoiceOpen(false);
                requireLoginOrContinue({
                  isAuthenticated: false,
                  location: { pathname: "/chemistry-test/info", search: "", hash: "" },
                  navigate,
                  fallback: "/chemistry-test/info",
                });
              }}>
                {tr("로그인하고 진행", "Sign In and Continue")}
              </button>
              <button type="button" className="chem-package-pay-btn" onClick={() => {
                enableGuestAnalysisMode();
                getGuestPaymentId();
                setGuestMode(true);
                setLoginChoiceOpen(false);
                handleAnalysis({ guestOverride: true });
              }}>
                {tr("비회원으로 진행", "Continue as Guest")}
              </button>
            </div>
          </div>
        </div>,
        document.body,
      ) : null}

      {packageModalOpen ? (
        <ChemistryPackageModal
          products={chemistryPaymentProducts}
          onClose={() => setPackageModalOpen(false)}
          onSelect={openPaymentForProduct}
        />
      ) : null}
    </div>
  );
}

function ChemistryPackageModal({ products, onClose, onSelect }) {
  const { i18n } = useTranslation();
  const language = i18n.resolvedLanguage || i18n.language;
  const tr = (ko, en) => localize(language, { ko, en });
  if (typeof document === 'undefined') return null;

  return createPortal(
    <div className="chem-package-modal-backdrop" role="presentation" onClick={onClose}>
      <div
        className="chem-package-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="chem-package-title"
        onClick={(event) => event.stopPropagation()}
      >
        <div className="chem-package-modal-header">
          <span className="chem-premium-badge">Premium</span>
          <button type="button" className="chem-package-close" onClick={onClose} aria-label={tr('닫기', 'Close')}>
            x
          </button>
          <h2 id="chem-package-title">{tr('케미 분석 + 프리미엄 해석 이용권', 'Chemistry Analysis + Premium Report Pass')}</h2>
          <p>{tr('추가 분석 횟수를 선택하세요. 각 분석 결과의 프리미엄 해석이 함께 열립니다.', 'Choose the number of additional analyses. Each result includes its premium report.')}</p>
        </div>

        <div className="chem-package-stack">
          {products.map((product) => (
            <div
              key={product.id}
              className={"chem-package-card " + (product.recommended ? "is-recommended" : "")}
            >
              <div className="chem-package-card-top">
                <div>
                  <strong>{language.startsWith('en') ? product.title_en : product.title}</strong>
                  <span>{language.startsWith('en') ? product.tagline_en : product.tagline}</span>
                </div>
                {product.badge ? <em>{language.startsWith('en') ? product.badge_en : product.badge}</em> : null}
              </div>
              <div className="chem-package-price-row">
                <span>{formatProductPrice(product, language)}</span>
                <small>{formatPerUsePrice(product, language)}</small>
              </div>
              <p>{language.startsWith('en') ? product.description_en : product.description}</p>
              <button
                type="button"
                className={"chem-package-pay-btn " + (product.recommended ? "is-recommended" : "")}
                onClick={() => onSelect(product.id)}
              >
                {tr('결제하기', 'Continue to Payment')}
              </button>
            </div>
          ))}
        </div>
      </div>
    </div>,
    document.body,
  );
}

function slotCardStyle(borderColor) {
  return {
    background: 'rgba(30, 41, 59, 0.4)',
    backdropFilter: 'blur(12px)',
    border: `1px dashed ${borderColor}`,
    borderRadius: '20px',
    padding: '1.15rem',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'stretch',
    gap: '0.85rem',
    textAlign: 'left',
    minWidth: 0,
  };
}

const slotHeaderStyle = {
  display: 'flex',
  justifyContent: 'space-between',
  gap: '0.75rem',
  alignItems: 'flex-start',
  flexWrap: 'wrap',
};

const slotDescriptionStyle = {
  margin: '0.25rem 0 0',
  color: '#94a3b8',
  fontSize: '0.88rem',
  lineHeight: 1.5,
};

const nameInputStyle = {
  width: '100%',
  padding: '0.85rem 1rem',
  borderRadius: '12px',
  background: 'rgba(255, 255, 255, 0.05)',
  border: '1px solid rgba(255, 255, 255, 0.1)',
  color: '#f8fafc',
  outline: 'none',
  fontSize: '0.95rem',
};

function previewLabelStyle(hasPreview) {
  return {
    width: '100%',
    aspectRatio: '1',
    borderRadius: '16px',
    background: hasPreview ? 'transparent' : 'rgba(255,255,255,0.02)',
    border: '1px solid rgba(255,255,255,0.08)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    cursor: 'pointer',
    overflow: 'hidden',
    position: 'relative',
  };
}

const placeholderCopyStyle = {
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  color: '#64748b',
};

const nativePickerButtonStyle = {
  flex: 1,
  borderRadius: '12px',
  border: '1px solid rgba(255,255,255,0.12)',
  background: 'rgba(15, 23, 42, 0.85)',
  color: '#e2e8f0',
  padding: '0.88rem 0.9rem',
  fontSize: '0.94rem',
  fontWeight: '700',
  cursor: 'pointer',
};

function messageBoxStyle(type) {
  const base =
    type === 'error'
      ? {
          background: 'rgba(239, 68, 68, 0.1)',
          border: '1px solid rgba(239, 68, 68, 0.3)',
          color: '#fca5a5',
        }
      : {
          background: 'rgba(59, 130, 246, 0.12)',
          border: '1px solid rgba(96, 165, 250, 0.3)',
          color: '#bfdbfe',
        };

  return {
    ...base,
    padding: '1rem 1.1rem',
    borderRadius: '12px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: '0.8rem',
    flexWrap: 'wrap',
  };
}

const resetButtonStyle = {
  borderRadius: '999px',
  border: '1px solid rgba(191, 219, 254, 0.35)',
  background: 'rgba(15, 23, 42, 0.7)',
  color: '#eff6ff',
  padding: '0.6rem 1rem',
  fontSize: '0.9rem',
  fontWeight: '700',
  cursor: 'pointer',
};

export default ChemistryInfo;
